/**
 * CONTRIBUTION GUIDE:
 *
 * - Please follow naming conventions outlined below
 * - Please add selectors to the appropriate constant (ui vs. data vs edge case)
 * - Please keep selectors organized alphabetically
 * - Please include a code comment above the selector describing the purpose of the component
 * - For edge case selectors, please provide details on
 *    - Why the selector is necessary
 *    - How it is used
 *    - Who was the selector designed specifically to be consumed by
 */

/**
 * UI_COMPONENT_SELECTORS should be placed on any presentational component located in the
 * components/ui directory. Selectors should always be named using the convention
 * duet--[context]--[component-name] where the context represents how the component is
 * categorized within the UI directory. Since the convention of the UI component directory
 * structure is to place components in a directory that matches their context, this should
 * always match the name of the directory in which the component is placed. The directory
 * name (context) and component name should always being kebab cased in the selector:
 *
 * Convention: duet--[context]--[component-name]
 * Component Path: src/components/ui/article/ArticleByline
 * Selector: "duet--article--article-byline"
 *
 * For components with subcomponents, such as the Navigation component:
 *
 * Convention: duet--[context]--[component-name]--[subcomponent-name].
 * Component Path: /ui/navigation/Navigation/components/NavigationDrawer
 * Selector: "duet--navigation--navigation--navigation-drawer"
 *
 */
const UI_COMPONENT_SELECTORS = {
  /**
   * methodology accordion body component
   */
  Accordion: "duet--article--accordion",

  /**
   * action box article body component
   */
  ActionBox: "duet--article--action-box",

  /**
   * the article byline containing author name and timestamp
   */
  ArticleByline: "duet--article--article-byline",

  /**
   * gallery article body component
   */
  ArticleGallery: "duet--article--gallery",

  /**
   * TODO: Add description
   */
  ArticleGroups: "duet--article--article-groups",

  /**
   * Lockup of article taxonomy and latest articles recircs
   */
  ArticleRecirc: "duet--layout--article-recirc",

  /**
   * blockquote article body component
   */
  Blockquote: "duet--article--blockquote",

  /**
   * Alignment of images and components within articles
   */
  BlockPlacement: "duet--article--block-placement",

  /**
   * Disclaimer shown at top of branded content bodies
   */
  BrandedContentDisclaimer: "duet--article--branded-content-disclaimer",

  /**
   * Sponsor logo displayed in branded article ledes
   */
  BrandedContentSponsor: "duet--cta--branded-content-sponsor",

  /**
   * Lede for branded articles
   */
  BrandedLede: "duet--ledes--branded-lede",

  /**
   * byline on a breaker component
   */
  BreakerByline: "duet--recirculation--breaker-byline",

  /**
   * background box and title on verge specific breaker
   */
  BreakerPattern: "duet--recirculation--breaker",

  /**
   * breaking news banner component
   */
  BreakingNewsBanner: "duet--cta--breaking-news-banner",

  /**
   * CTA buttons
   */
  Button: "duet--cta--button",

  /**
   * CTA link buttons
   */
  LinkButton: "duet--cta--link-button",

  /**
   * caption for media displaying caption and image credit
   */
  Caption: "duet--media--caption",

  /**
   * link to Coral comments
   */
  CommentsLink: "duet--article--comments-link",

  /**
   * Contact page mega-form
   */
  ContactForm: "duet--article--contact-form",

  /**
   * Article button for Coral comments
   */
  ArticleCommentsButton: "duet--article--comments-button",

  /**
   * warning for sensitive media
   */
  ContentWarning: "duet--media--content-warning",

  /**
   * TODO: Add description
   */
  ContentCardGroup: "duet--content-cards--content-card-group",

  /**
   * TODO: Add description
   */
  ContentCardPlayButton: "duet--content-cards--content-card-play-button",

  /**
   * TODO: Add description
   */
  ContentCardScore: "duet--content-cards--content-card-score",

  /**
   * TODO: Add description
   */
  ContentCardStreamLabel: "duet--content-cards--content-card-stream-label",

  /**
   * Popup banner for users to provide cookie/tracking consent (GDPR)
   */
  CookieBanner: "duet--cta--cookie-banner",

  /**
   * Entry group curated showcase cards section that lives on the game and PG homepage for games and packages
   */
  CuratedShowcaseCards: "duet--layout--curated-showcase-cards",

  /**
   * component for rendering html from the cms
   */
  DangerouslySetCMSMarkup: "duet--article--dangerously-set-cms-markup",

  /**
   * notifies the user that an article includes affiliate links
   */
  Disclaimer: "duet--article--disclaimer",

  /**
   * Divider
   */
  Divider: "duet--article--divider",

  /**
   * html embed from external media source
   */
  Embed: "duet--media--embed",

  /**
   * layout component that contains the entry body components of an entry
   */
  ArticleBody: "duet--layout--entry-body",

  /**
   * layout component containing the entry body and rail of an entry
   */
  ArticleBodyContainer: "duet--layout--entry-body-container",

  /**
   * entry image component
   */
  EntryImage: "duet--layout--entry-image",

  /**
   * TODO: Add description
   */
  FeatureDek: "duet--article--feature-dek",

  /**
   * TODO: Add description
   */
  FeatureHeadline: "duet--article--feature-headline",

  /**
   * Lede for feature articles
   */
  FeatureLede: "duet--ledes--feature-lede",

  /**
   * Lede for special feature articles
   */
  SpecialFeatureLede: "duet--ledes--special-feature-lede",

  /**
   * Lede for overlay (feature) articles
   */
  SpecialFeatureOverlayLede: "duet--ledes--feature-lede-overlay",

  /**
   * site-wide footer
   */
  Footer: "duet--navigation--footer",

  /**
   * PMC acquisition privacy notice pinned to the bottom of the viewport
   */
  PmcPrivacyBanner: "duet--navigation--pmc-privacy-banner",

  /**
   * Container for the 4 grid article pattern on the homepage
   */
  FourGridContainer: "duet--layout--four-grid-container",

  /**
   * header used in layouts
   */
  Header: "duet--layout--header",

  /**
   * Lockup of header + content card pattern
   */
  HeaderPattern: "duet--layout--header-pattern",

  /**
   * Layout for In-5 game
   */
  In5Game: "duet--layout--in5game",

  /**
   * Page header used in layouts
   */
  PageHeader: "duet--layout--page-header",

  /**
   * Highlight block used in articles
   */
  Highlight: "duet--article--highlight",

  /**
   * TODO: Add description
   */
  ImageComparison: "duet--article--image-comparison",

  /**
   * A single image in an image gallery
   */
  ImageGalleryImage: "duet--article--image-gallery-image",

  /**
   * TODO: Add description
   */
  ImageGalleryFourUp: "duet--article--image-gallery-four-up",

  /**
   * Image gallery with a 1x3 row of images
   */
  ImageGalleryThreeUp: "duet--article--image-gallery-three-up",

  /**
   * Image gallery with two images in one row and one image below
   */
  ImageGalleryThreeUpBottom: "duet--article--image-gallery-three-up-bottom",

  /**
   * TODO: Add description
   */
  ImageGalleryTwoUp: "duet--article--image-gallery-two-up",

  /**
   * lead paragraph of an article body
   */
  LeadParagraph: "duet--article--lead-paragraph",

  /**
   * TODO: Add description
   */
  LedeBackground: "duet--article--lede-background",

  /**
   * Featured product table in articles reviewing multiple products
   */
  FeaturedProductsTable: "duet--article--featured-product-table",

  /**
   * Individual cards in featured product table
   */
  FeaturedProductCard: "duet--article--featured-product-card",

  /**
   * Select input for forms
   */
  FormFieldSelect: "duet--cta--form-field-select",

  /**
   * Text input for forms
   */
  FormFieldText: "duet--cta--form-field-text",

  /**
   * Text input for forms
   */
  FormFieldTextarea: "duet--cta--form-field-textarea",

  /**
   * Lede for map articles
   */
  MapLede: "duet--ledes--map-lede",

  /**
   * Component for rendering a venue card on a map article
   */
  MapCard: "duet--article--map-card",

  /**
   * Recirc used to showcase articles that featured the venue as well
   */
  MapCardRecirc: "duet--recirculation--map-card-recirc",

  /**
   * Recirculation with displayed below the map
   */
  MapFooterRecirc: "duet--recirculation--map-footer-recirc",

  /**
   * Component for rendering meta data about a object, like a video game's release date
   */
  MetadataStrip: "duet--meta--metadata-strip",

  /**
   * minimal navigation component
   */
  MinimalNav: "duet--navigation--minimal-nav",

  /**
   * primary site-wide navigation
   */
  Navigation: "duet--navigation--navigation",

  /**
   * slide-out panel activated by the main and sticky navigation components
   */
  NavigationDrawer: "duet--navigation--navigation-drawer",

  /**
   * newsletter signup module
   */
  Newsletter: "duet--cta--newsletter",

  /**
   * ordered list article body component
   */
  OrderedList: "duet--article--ordered-list",

  /**
   * Lede for packages
   */
  PackageLede: "duet--ledes--package-lede",

  /**
   * Pagination lockup for e.g. group pages
   */
  Pagination: "duet--navigation--pagination",

  /**
   * Podcast component used to render a list of podcasts
   */
  PodcastSection: "duet--media--podcast-section",

  /**
   * product card article body component
   */
  ProductCard: "duet--article--product-card",

  /**
   * Hype Desk product block variant (Verge only)
   */
  HypeDeskProductCard: "duet--article--hype-desk-product-card",

  /**
   * product card article body component
   */
  ProductGrid: "duet--article--product-grid",

  /**
   * pullquote article body component
   */
  Pullquote: "duet--article--article-pullquote",

  /**
   * QuickLinks component for the home page
   */
  QuickLinks: "duet--cta--quick-links",

  /**
   * layout component containing content which appears in the "rail" adjacent to a "river",
   * the rail may in some cases get stacked to be part of a single column along with the river
   * on smaller viewport sizes
   */
  Rail: "duet--layout--rail",

  /**
   * A component used by Vox.com to display a rating for a thing (e.g. 3/5 'Voxes')
   */
  Rating: "duet--article--rating",

  /**
   * Content wrapper with max height and "read more" reveal button
   */
  ReadMoreWrapper: "duet--cta--read-more-wrapper",

  /**
   * Parsely powered recommended articles component
   */
  RecommendedArticles: "duet--recirculation--recommended-articles",

  /**
   * Related article body component
   */
  Related: "duet--article--related",
  /**
   * layout component containing the "river" of content cards recirc below articles/hubs
   */
  River: "duet--layout--river",

  /**
   * component which contains the rivers and rails for any layouts that use them
   */
  RiverContainer: "duet--layout--river-container",

  /**
   * recirc component used to display a list of related content
   */
  RelatedList: "duet--recirculation--related-list",

  /**
   * Component that renders different patterns at different breakpoints
   */
  PatternSwitcher: "duet--layout--pattern-switcher",

  /**
   * score card article body component
   */
  Scorecard: "duet--article--scorecard",

  /**
   * buttons for sharing articles
   */
  ShareButtons: "duet--article--share-buttons",

  /**
   * Showcase card component used in the curated showcase cards section
   */
  ShowcaseCard: "duet--content-cards--showcase-card",

  /**
   * Stream table of contents
   */
  StreamHighlights: "duet--navigation--stream-highlights",

  /**
   * Entry group recirculation component that lives in the right rail
   */
  RightRailRecirc: "duet--recirculation--right-rail-recirc",
  /**
   * Keyboard-accessible button that lets users skip over navigation to main content area
   */
  SkipToContent: "duet--cta--skip-to-content",

  /**
   * Simple link component typically used in larger components
   */
  StandaloneLink: "duet--navigation--standalone-link",

  /**
   * thin divider used to divide layouts
   */
  StandardDivider: "duet--layout--standard-divider",

  /**
   * article heading
   */
  StandardHeading: "duet--article--standard-heading",

  /**
   * Lede for standard articles
   */
  StandardLede: "duet--ledes--standard-lede",

  /**
   * Lede for standard articles
   */
  StandardLedeBottom: "duet--ledes--standard-lede-bottom",

  /**
   * paragraph article body component
   */
  StandardParagraph: "duet--article--standard-paragraph",

  /**
   * sticky navigation component
   */
  StickyNav: "duet--navigation--sticky-nav",

  /**
   * Header section above quickposts on river/homepage
   */
  StorystreamHeader: "duet--recirculation--storystream-header",

  /**
   * Flashing "live now" beacon appearing in StorystreamHeader
   */
  StorystreamHeaderBeacon: "duet--recirculation--storystream-header-beacon",

  /**
   * Lede for streams
   */
  StreamLede: "duet--ledes--stream-lede",

  /**
   * table article body component
   */
  Table: "duet--article--table",

  /**
   * table of contents article body component
   */
  TableOfContents: "duet--article--table-of-contents",

  /**
   * article published timestamp
   */
  Timestamp: "duet--article--timestamp",

  /**
   * Toggle switch component (UI chrome for a checkbox)
   */
  Toggle: "duet--cta--toggle",

  /**
   * Compact circular toggle component with plus/checkmark icons
   */
  ToggleCompact: "duet--cta--toggle-compact",

  /**
   * unordered list article body component
   */
  UnorderedList: "duet--article--unordered-list",

  /**
   * Link bar with icons used on the home page
   */
  UtilityBar: "duet--navigation-utility-bar",

  /**
   * Venue card component used in posts with tagged venues
   */
  VenueCard: "duet--content-cards--venue-card",

  /**
   * html embed from external video source
   */
  VideoEmbed: "duet--media--video-embed",

  /**
   * Pattern to render a list of videos with playable thumbs
   */
  VideoSection: "duet--media--video-section",

  /**
   * Pattern to render a video with a title and description
   */
  FeaturedVideoSection: "duet--media--featured-video-section",

  /**
   * wiki banner used for contributions and other yet to be determined use cases
   */
  WikiBanner: "duet--navigation--wiki-banner",

  /**
   * Loop Nav used for specialized navigation on package articles and other contexts
   */
  LoopNav: "duet--navigation--loop-nav",

  /**
   * Category spotlight component
   */
  CategorySpotlight: "duet-page-layout--category-spotlight",

  /**
   * Wrapper for boosted post articles
   */
  BoostedPostWrapper: "duet--article--boosted-post-wrapper",

  /**
   * Wrapper for boosted post articles when expanded
   */
  BoostedPostWrapperExpanded: "duet--article--boosted-post-wrapper-expanded",

  /**
   * Button to expand a collapsed boosted post
   */
  BoostedPostCollapseButton: "duet--article--boosted-post-collapse-button",
};

/**
 * DATA_COMPONENT_SELECTORS represent selectors placed on components which include
 * GraphQL fragments and are tightly coupled with Wordpress data. These selectors should
 * only be used when necessary to accomplish a use case that cannot be achieved using
 * component selectors on UI (presentational) components. The naming convention for
 * these selectors should follow the convention of the UI components, which may require
 * some approximation as to how the component would be categorized and named within
 * the components/ui directory.
 */
const DATA_COMPONENT_SELECTORS = {
  /**
   * the page layout for author profile archives
   */
  AuthorProfileArchiveLayout: "duet--page-layout--author-archive",

  /**
   * the page layout for author profiles
   */
  AuthorProfileLayout: "duet--page-layout--author",

  /**
   * the page layout for entries with branded content
   */
  BrandedArticleContainer: "duet--page-layout--branded-article",

  /**
   * the page layout for category pages
   */
  CategoryLayout: "duet--page-layout--category",

  /**
   * the page layout for the city directory page
   */
  CityDirectoryLayout: "duet--page-layout--city-directory",

  /**
   * the page layout for communities directory page
   */
  CommunitiesLayout: "duet--page-layout--communities",

  /**
   * the page layout for embed story content types
   */
  EmbedStoryLayout: "duet--page-layout--embed-story",

  /**
   * Default layout for entry pages
   */
  EntryLayout: "duet--page-layout--entry",

  /**
   * the sidebar that can appear in articles
   */
  EntryBodySidebar: "duet--article--sidebar",

  /**
   * the page layout for hub pages and entry groups
   */
  EntryGroupLayout: "duet--page-layout--group",

  /**
   * the page layout for entry group hub pages
   */
  EntryGroupHubLayout: "duet--page-layout--group-hub",

  /**
   * the rail to the right of article bodies
   */
  EntryRailComponents: "duet--article--rail",

  /**
   * the fan post feed layout
   */
  FanPostFeedLayout: "duet--page-layout--fan-post-feed",

  /**
   * the fan post layout
   */
  FanPostLayout: "duet--page-layout--fan-post",

  /**
   * the default page layout for entries
   */
  FeatureArticleContainer: "duet--page-layout--feature-article",

  /**
   * the page layout for front page archive pages
   */
  FrontPageArchiveLayout: "duet--page-layout--front-page-archive",

  /**
   * the page layout for front pages
   */
  FrontPageLayout: "duet--page-layout--front-page",

  /**
   * the default page layout for full archive
   */
  FullArchiveLayout: "duet--page-layout--full-archive",

  /**
   * The hero component that appears on the
   */
  HeroContainer: "duet--article--hero",

  /**
   * the page layout for the home page
   */
  HomepageLayout: "duet--page-layout--homepage",

  /**
   * the page layout for the home page archive pages
   */
  HomepageArchiveLayout: "duet--page-layout--homepage-archive",

  /**
   * Homepage latest quickposts
   */
  HomepageLatestQuickposts: "duet--homepage-latest-quickposts",

  /**
   * Used for Homepage Top Curated Content Sections
   */
  HomepageTopCuratedContent: "duet--homepage-top-curated-content-section",

  /**
   * Used for Homepage Bottom Curated Content Sections
   */
  HomepageBottomCuratedContent: "duet--homepage-bottom-curated-content-section",

  /**
   * Homepage Category Shelves Container
   */
  HomepageCategoryShelvesContainer: "duet--homepage-category-shelves-container",

  /**
   * Homepage category shelves right rail — sections for dynamic ad placement
   */
  HomepageCategoryShelvesRailSection:
    "duet--homepage-category-shelves-rail-section",

  /**
   * The homepage hero component
   */
  HomepageHero: "duet--homepage--hero",

  /**
   * These selectors are applied to specific instances of the feature ledes used in feature
   * articles to allow placement depending on the type of lede.
   */
  LedeHeadlineOverlay: "duet--article--lede-headline-overlay",

  /**
   * this selector would apply to all lede images
   */
  LedeImage: "duet--article--lede-image",

  /**
   * Lede image for the split left/split right feature layouts
   */
  LedeSplit: "duet--article--lede-split",

  /**
   * the page layout for map post types
   */
  MapPageLayout: "duet--page-layout--map",

  /**
   * the fight sections for MMA event post types
   */
  MMAEventFightSections: "duet--mma-event--fight-sections",

  /**
   * the page layout for MMA event post types
   */
  MMAEventLayout: "duet--page-layout--mma-event-page",

  /**
   * the default page layout for mma fighter page
   */
  MMAFightCardPageLayout: "duet--page-layout--mma-fight-card-page",

  /**
   * the fighter stats for mma fighter & fight pages
   */
  MMAFighterStats: "duet--mma-fighter--stats",

  /**
   * the default page layout for mma fighter page
   */
  MMAFighterPageLayout: "duet--page-layout--mma-fighter-page",

  /**
   * the page layout for MMA schedule post types
   */
  MMAScheduleResultsLayout: "duet--page-layout--mma-schedule-results-page",

  /**
   * the page layout for MMA weight class post types
   */
  MMAWeightClassLayout: "duet--page-layout--mma-weight-class-page",

  /**
   * the native ad that appears at the bottom of the related stories component on articles
   */
  NativeAdLinkset: "duet--ad--native-ad-linkset",

  /**
   * the native ad that appears in the right rail component on articles
   */
  NativeAdRail: "duet--ad--native-ad-rail",

  /**
   * The native ad that appears within the river on the homepage
   */
  NativeAdContentLink: "duet--ad--native-content-link",

  /**
   * The native ad that appears in the fanpost page
   */
  NativeAdFanPost: "duet--ad--native-fan-post",

  /**
   * the page layout for neighborhood post types
   */
  NeighborhoodLayout: "duet--page-layout--neighborhood",

  /**
   * the page layout for neighborhood directory page
   */
  NeighborhoodDirectoryLayout: "duet--page-layout--neighborhood-directory",

  /**
   * Newsletter card for the newsletters page
   */
  NewslettersPageCard: "duet--cta--newsletters-page-card",

  /**
   * Newsletters page popup subscription form
   */
  NewslettersPagePanel: "duet--cta--newsletters-page-panel",

  /**
   * the page layout for entries with packageToc data
   */
  PackageContainer: "duet--page-layout--package",

  /**
   * the page layout for package post types
   */
  PackageLayout: "duet--page-layout--package",

  /**
   * the page layout for entries of the PAGE type
   */
  PageLayout: "duet--page-layout--page",

  /**
   * the page layout for standard post types
   */
  PostLayout: "duet--page-layout--post",

  /**
   * the page layout for dining report post types
   */
  DiningReportLayout: "duet--page-layout--dining-report",

  /**
   * the page layout for venue post types
   */
  VenuePageLayout: "duet--page-layout--venue",

  /**
   * the page layout for package post types
   */
  VideoGamePageLayout: "duet--page-layout--gamepage",

  /**
   * the page layout for video post types
   */
  VideoPostLayout: "duet--page-layout--video-post",

  /**
   * the page layout for entries of the QUICK_POST type
   */
  QuickPostLayout: "duet--page-layout--quick-post",

  /**
   * the page layout for entries with the standard layout template
   */
  StandardArticleContainer: "duet--page-layout--standard-article",

  /**
   * the page layout for stream types
   */
  StreamLayout: "duet--page-layout--stream",

  /**
   * the page layout for sub-community home pages
   */
  SubcommunityHomepageLayout: "duet--page-layout--subcommunity-homepage",

  /**
   * Map search layout
   */
  MapSearchLayout: "duet--page-layout--map-search",

  /**
   * Variable story set layout used for the homepage recirc section that features different content sets
   */
  VariableStorySet: "duet--homepage--variable-story-set",

  /**
   * Most popular section used for the homepage recirc section that features the most popular content
   */
  MostPopular: "duet--homepage--most-popular",

  /**
   * Most discussed section used for the homepage recirc section that features the most discussed content
   */
  MostDiscussed: "duet--homepage--most-discussed",

  /**
   * Category shelf section used for the homepage recirc section that features content from a specific category
   */
  CategoryShelf: "duet--homepage--category-shelf",
};

/**
 * EXTERNAL_COMPONENT_SELECTORS are selectors which handle edge cases to fulfill
 * requirements that meet specific business needs. Namely, selectors used for
 * editorial CSS overrides or dynamic ad placement. As a result, these selectors
 * may slightly break conventions (naming or usage) and this should be specified
 * in the comments.
 */
const EXTERNAL_COMPONENT_SELECTORS = {
  /**
   * This selector is applied to the container at the root of the application in the
   * _app file which renders all layouts. It is used as a targeting selector for
   * prelude ads.
   */
  AppContainer: "duet--app",

  /**
   * This selector is applied to `body` whenever the sticky nav is shown.
   */
  BodyStickyNav: "duet--sticky-nav",

  /**
   * This selector is applied to the container of EntryBodyComponentPlacement, which
   * is a data component that is a wrapper around all entry (article) body components.
   * This selector is mainly used for dynamic ad placement, where the ad team needs
   * to be able to select every (n) article body components, without a requirement
   * of any specific knowledge as to what type of article body components are being
   * selected.
   */
  ArticleBodyComponent: "duet--article--article-body-component",

  /**
   * This selector is used by the ads team to target the entire container which wraps
   * all article body components
   */
  ArticleBodyComponentContainer:
    "duet--article--article-body-component-container",

  /**
   * This selector is the wrapper for the date and comments elements in the article lede.
   * It is used to dynamically place article sponsorship ads.
   */

  ArticleDateAndComments: "duet--article--date-and-comments",

  /**
   * This selector is applied to all variations of ledes used in articles, regardless
   * of entry type. Editorial will mainly need to be able to select components in the
   * ledes (mainly FeatureLede) to apply CSS overrides. Since the overrides are done in
   * the CMS, knowing specifically what type of lede it is (FeatureLede vs StandardLede
   * or HeadlineAbove vs HeadlineOverlay) is not important to them, they just need to be
   * able to target the lede and elements within the lede. To solve this use case,
   * we apply the selector on all article lede component variations.
   */
  ArticleLede: "duet--article--lede",

  /**
   * Article recirc color container
   */
  ArticleRecircColorContainer: "duet--layout--article-recirc-color-container",

  /**
   * This selector is applied to all variations of content cards to allow better ease of
   * selection for css override purposes
   */
  ContentCard: "duet--content-cards--content-card",

  /**
   * This selector is used by the ad team to target the description on entry groups
   * which are currently sponsored
   */
  EntryGroupDescription: "duet--group--description",

  /**
   * This selector is used by the ad team to target the maps container placed on the homepage
   */
  HomepageMapsContainer: "duet--homepage--maps-container",

  /**
   * This selector is used by the ad team to target the standardfanpost container placed on the homepage
   */
  HomepageStandardFanpostContainer:
    "duet--homepage--standard-fanpost-container",

  /**
   * This selector is used by the ad team to target the utility bar placed on the homepage
   */
  HomepageUtilityBarContainer: "duet--homepage--utility-bar-container",

  /**
   * One of these selectors is applied to articles with the headline above feature lede layout dependent
   * on if the background color is blurple or franklin. These selectors are used by the ad team to place
   * the article sponsorship slot.
   */

  LedeHeadlineAboveBlurple: "duet--article--lede-headline-above-blurple",

  LedeHeadlineAboveFranklin: "duet--article--lede-headline-above-franklin",

  /**
   * This selector is used to target the map container
   */
  MapContainer: "duet--article--map-container",

  /**
   * This selector is used to target different components rendered by the "MoreStories"
   * component which includes StreamMemberEntries, PackageMemberToc and RelatedStories
   */
  MoreStories: "duet--article--more-stories",

  /**
   * This selector is applied to the container of packageToc in package articles to allow for
   * easier targeting of package content below article body components
   */
  PackageLandingPageToc: "duet--article--package-toc",

  /**
   * This selector is applied to QuickPosts.
   */
  QuickPost: "duet--content-cards--quick-post",
};

const SELECTORS = {
  ...UI_COMPONENT_SELECTORS,
  ...DATA_COMPONENT_SELECTORS,
  ...EXTERNAL_COMPONENT_SELECTORS,
};

export default SELECTORS;
